import {
  fetchStaticInteractedResponse,
  shouldUseStaticInteracted,
} from "./staticInteractedCustomer";
import { clearSession, getAuthHeaders, getToken } from "../utils/auth";


/** Routes that must not send Authorization (pre-login). */
const NO_AUTH_PATHS = [
  "/getInteractedCustomerDetail/loginbusiness",
  "/getInteractedCustomerDetail/send-otp",
  "/getInteractedCustomerDetail/verify-otp",
];

const getBaseUrl = () => {
  const configured = process.env.REACT_APP_APIHUB_BASE_URL;
  if (configured !== undefined && configured !== "") {
    return configured.replace(/\/$/, "");
  }
  else return "";
};

const buildUrl = (path) =>
  `${getBaseUrl()}${path.startsWith("/") ? "" : "/"}${path}`;

const pathNeedsAuth = (path) => {
  const pathname = path.split("?")[0];
  return !NO_AUTH_PATHS.some(
    (skip) => pathname === skip || pathname.endsWith(skip),
  );
};

async function withStaticInteractedFallback(
  method,
  path,
  payload,
  liveRequest,
) {
  const useStatic = shouldUseStaticInteracted();

  if (useStatic) {
    const staticBody = await fetchStaticInteractedResponse(
      method,
      path,
      payload,
    );
    if (staticBody !== null) return staticBody;

    if (process.env.REACT_APP_STATIC_ONLY === "true") {
      throw new Error(
        `Static-only mode: no mock found for ${method} ${path}. Add a matching file under public/getInteractedCustomerDetail/.`,
      );
    }
  }

  try {
    return await liveRequest();
  } catch (err) {
    if (useStatic) {
      const fallback = await fetchStaticInteractedResponse(
        method,
        path,
        payload,
      );
      if (fallback !== null) return fallback;
    }
    throw err;
  }
}

/**
 * @param {string} method
 * @param {string} path
 * @param {{ body?: unknown; isFormData?: boolean }} [options]
 */
async function apihubFetch(method, path, options = {}) {
  const { body, isFormData = false } = options;
  const url = buildUrl(path);
  const includeAuth = pathNeedsAuth(path);
  const headers = {
    ...getAuthHeaders(includeAuth),
  };

  if (body !== undefined && !isFormData) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(url, {
    method,
    headers,
    body:
      body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
  });

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const responseBody = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const message =
      (isJson &&
        responseBody &&
        (responseBody.message || responseBody.error)) ||
      `${res.status} ${res.statusText}`;
    const sessionExpired =
      res.status === 401 &&
      getToken() &&
      /access token required|token expired|invalid token|^unauthorized$/i.test(
        String(message),
      );
    if (sessionExpired) {
      clearSession();
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.includes("/login")
      ) {
        window.location.assign("/login");
      }
    }
    throw new Error(message);
  }

  return responseBody;
}

export async function apihubGet(path) {
  return withStaticInteractedFallback("GET", path, undefined, () =>
    apihubFetch("GET", path),
  );
}

export async function apihubPost(path, payload = {}) {
  return withStaticInteractedFallback("POST", path, payload, () =>
    apihubFetch("POST", path, { body: payload }),
  );
}

async function apihubJson(method, path, payload) {
  return withStaticInteractedFallback(method, path, payload, () =>
    apihubFetch(method, path, { body: payload }),
  );
}

export function apihubPut(path, payload) {
  return apihubJson("PUT", path, payload);
}

export function apihubPatch(path, payload) {
  return apihubJson("PATCH", path, payload);
}

export function apihubDelete(path, payload) {
  return apihubJson("DELETE", path, payload);
}

/** Root-level routes (e.g. /send-textonly) used by live chat. */
export function apihubRootPost(path, payload) {
  return apihubJson("POST", path, payload);
}

export async function apihubRootUpload(path, formData) {
  return withStaticInteractedFallback("POST", path, undefined, () =>
    apihubFetch("POST", path, { body: formData, isFormData: true }),
  );
}

/** Multipart upload (e.g. CSV campaign files). */
export async function apihubUpload(path, formData) {
  return withStaticInteractedFallback("POST", path, undefined, () =>
    apihubFetch("POST", path, { body: formData, isFormData: true }),
  );
}
